// Bảng chọn emoji của ô soạn tin chat khách.
//
// Icon của chính bảng (nút mở, thẻ nhóm) là icon vector lucide — KHÔNG dùng
// ký tự emoji làm icon giao diện: emoji do bộ chữ của máy vẽ nên mỗi hệ điều
// hành một hình, và không nhận màu theo token. Emoji chỉ xuất hiện ở LƯỚI,
// nơi nó là nội dung người dùng chọn để gửi.
import { useMemo, useState } from "react";
import { Briefcase, Camera, Clock, Hand, Hash, Heart, Smile, Sticker as StickerIcon } from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import {
  EMOJI_GROUPS,
  emojiKeyword,
  recentEmojis,
  rememberEmoji,
  searchEmoji,
  type EmojiEntry,
  type EmojiGroupKey,
} from "./emoji-catalog";
import { STICKERS, type Sticker } from "./stickers/stickers";

/** Icon của từng thẻ nhóm. Khai riêng để kho emoji giữ được là tệp .ts thuần. */
const GROUP_ICON: Record<EmojiGroupKey, typeof Smile> = {
  recent: Clock,
  faces: Smile,
  people: Hand,
  love: Heart,
  photo: Camera,
  things: Briefcase,
  symbols: Hash,
};

const RECENT_LABEL = "Dùng gần đây";

/** Nhãn tiếng Việt của một nhóm — dùng cho thẻ, tiêu đề và aria-label. */
function groupLabel(key: EmojiGroupKey): string {
  if (key === "recent") return RECENT_LABEL;
  return EMOJI_GROUPS.find((g) => g.key === key)?.label ?? key;
}

const STICKER_LABEL = "Nhãn dán Gạo Nâu";

function GroupTabs({
  tabs,
  active,
  onPickGroup,
  stickerTab,
}: {
  tabs: readonly EmojiGroupKey[];
  active: EmojiGroupKey | "stickers";
  onPickGroup: (key: EmojiGroupKey | "stickers") => void;
  /** Có = thêm thẻ "Nhãn dán Gạo Nâu" ở cuối. */
  stickerTab: boolean;
}) {
  const stickersOn = active === "stickers";
  return (
    <div role="tablist" aria-label="Nhóm icon" className="flex gap-1 overflow-x-auto border-b px-2 py-1.5">
      {stickerTab && (
        <button
          type="button"
          role="tab"
          aria-selected={stickersOn}
          aria-label={STICKER_LABEL}
          title={STICKER_LABEL}
          onClick={() => onPickGroup("stickers")}
          className={`flex h-9 w-9 flex-none items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
            stickersOn ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60"
          }`}
        >
          <StickerIcon className="h-4 w-4" />
        </button>
      )}
      {tabs.map((key) => {
        const Icon = GROUP_ICON[key];
        const label = groupLabel(key);
        const on = key === active;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={on}
            aria-label={label}
            title={label}
            onClick={() => onPickGroup(key)}
            className={`flex h-9 w-9 flex-none items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
              on ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60"
            }`}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

function EmojiGrid({
  items,
  label,
  onPick,
}: {
  items: readonly EmojiEntry[];
  label: string;
  onPick: (ch: string) => void;
}) {
  return (
    <div
      className="grid max-h-56 grid-cols-7 gap-0.5 overflow-y-auto px-2 pb-2 sm:grid-cols-8"
      aria-label={label}
    >
      {items.map((item) => (
        <button
          key={item.ch}
          type="button"
          onClick={() => onPick(item.ch)}
          aria-label={item.kw ? `Chèn ${item.kw}` : "Chèn icon"}
          title={item.kw}
          className="flex aspect-square items-center justify-center rounded-md text-xl leading-none transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {item.ch}
        </button>
      ))}
    </div>
  );
}

function StickerGrid({ onPick }: { onPick: (sticker: Sticker) => void }) {
  return (
    <div className="grid max-h-56 grid-cols-3 gap-1.5 overflow-y-auto px-2 pb-2" aria-label={STICKER_LABEL}>
      {STICKERS.map((st) => (
        <button
          key={st.key}
          type="button"
          onClick={() => onPick(st)}
          aria-label={`Gửi nhãn dán ${st.label}`}
          title={st.label}
          className="flex items-center justify-center rounded-lg p-1 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <img src={st.src} alt="" className="h-auto w-full" draggable={false} />
        </button>
      ))}
    </div>
  );
}

export function EmojiPicker({
  onPick,
  onPickSticker,
  disabled,
}: {
  /** Chèn emoji vào ô soạn tin. Bảng KHÔNG tự đóng — chọn liên tiếp được. */
  onPick: (ch: string) => void;
  /** Có = thêm thẻ nhãn dán; bấm một nhãn là GỬI NGAY rồi đóng bảng. */
  onPickSticker?: ((sticker: Sticker) => void) | undefined;
  disabled?: boolean | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<readonly string[]>([]);
  const [group, setGroup] = useState<EmojiGroupKey | "stickers">("faces");
  const [query, setQuery] = useState("");

  // Đọc "gần đây" lúc MỞ, không lúc dựng: bảng nằm trong mọi khung chat nên
  // đọc lúc dựng là chạm localStorage cả khi người dùng không mở bảng, và
  // danh sách sẽ cũ nếu họ vừa chat ở thẻ trình duyệt khác.
  const openChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    const list = recentEmojis();
    setRecent(list);
    setGroup(list.length > 0 ? "recent" : "faces");
    setQuery("");
  };

  const pick = (ch: string) => {
    onPick(ch);
    setRecent(rememberEmoji(ch));
  };

  const searching = query.trim().length > 0;
  const hits = useMemo(() => searchEmoji(query), [query]);
  const recentEntries = useMemo<readonly EmojiEntry[]>(
    () => recent.map((ch) => ({ ch, kw: emojiKeyword(ch) })),
    [recent],
  );
  const tabs = useMemo<readonly EmojiGroupKey[]>(() => {
    const keys = EMOJI_GROUPS.map((g) => g.key);
    return recent.length > 0 ? ["recent", ...keys] : keys;
  }, [recent.length]);

  const showStickers = !searching && group === "stickers" && Boolean(onPickSticker);
  const items = searching
    ? hits
    : group === "recent"
      ? recentEntries
      : group === "stickers"
        ? []
        : (EMOJI_GROUPS.find((g) => g.key === group)?.items ?? []);
  const heading = searching
    ? `Kết quả cho “${query.trim()}”`
    : group === "stickers"
      ? STICKER_LABEL
      : groupLabel(group);
  const pickSticker = (sticker: Sticker) => {
    setOpen(false);
    onPickSticker?.(sticker);
  };

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label="Chọn icon" disabled={disabled}>
          <Smile className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        // Trên điện thoại bảng bị đẩy sát mép phải nếu không chừa lề.
        collisionPadding={12}
        className="w-[min(20rem,calc(100vw-2rem))] p-0"
        // Chèn emoji xong thì ô soạn tin lấy lại focus để gõ tiếp — mà ô đó
        // nằm NGOÀI bảng (bảng vẽ qua portal), nên Radix mặc định coi là
        // focus ra ngoài và đóng bảng ngay sau cái emoji đầu tiên. Chặn đúng
        // đường đó thôi: bấm ra ngoài hay bấm Esc vẫn đóng như thường.
        onFocusOutside={(event) => event.preventDefault()}
      >
        <div className="border-b p-2">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Tìm icon: cảm ơn, tim, máy ảnh…"
            aria-label="Tìm icon theo từ khoá"
            className="h-9"
          />
        </div>

        {!searching && (
          <GroupTabs tabs={tabs} active={group} onPickGroup={setGroup} stickerTab={Boolean(onPickSticker)} />
        )}

        <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {heading}
        </p>

        {showStickers ? (
          <StickerGrid onPick={pickSticker} />
        ) : items.length === 0 ? (
          <p className="px-3 pb-3 text-sm text-muted-foreground">
            {searching ? "Không có icon nào khớp từ khoá này." : "Chưa dùng icon nào."}
          </p>
        ) : (
          <EmojiGrid items={items} label={heading} onPick={pick} />
        )}
      </PopoverContent>
    </Popover>
  );
}
